import { supabase } from '../lib/supabase';

/**
 * Service for managing webhook submissions from both hero and application forms
 * Handles unified form submissions for Make.com webhook integration
 */
class WebhookSubmissionService {
  
  /**
   * Submit hero form data (simple lead capture)
   * @param {Object} formData - Hero form data
   * @returns {Promise<{data?, error?}>}
   */
  async submitHeroForm(formData) {
    try {
      console.log('WebhookSubmissionService: Starting hero form submission', formData);
      
      // Validate required fields for hero form
      const requiredFields = ['fullName', 'email', 'phone', 'companyName'];
      
      for (const field of requiredFields) {
        if (!formData?.[field]) {
          console.error(`Missing required field: ${field}`);
          return { error: { message: `Missing required field: ${field}` } };
        }
      }

      // Prepare data for webhook table with proper field mapping
      const webhookData = {
        full_name: formData?.fullName,
        email: formData?.email,
        phone: formData?.phone,
        company_name: formData?.companyName,
        
        // Map tradeSpecialty correctly - this field can be nullable in webhook_submissions
        trade_specialty: formData?.tradeSpecialty || null,
        
        form_source: formData?.formSource || 'hero_form',
        submission_type: formData?.submissionType || 'lead',
        source: 'landing_page',
        
        // UTM and referrer tracking
        utm_source: formData?.utmSource || (typeof window !== 'undefined' ? new URLSearchParams(window?.location?.search)?.get('utm_source') : null),
        utm_medium: formData?.utmMedium || (typeof window !== 'undefined' ? new URLSearchParams(window?.location?.search)?.get('utm_medium') : null),
        utm_campaign: formData?.utmCampaign || (typeof window !== 'undefined' ? new URLSearchParams(window?.location?.search)?.get('utm_campaign') : null),
        referrer: formData?.referrer || (typeof window !== 'undefined' && typeof document !== 'undefined' ? document?.referrer || window?.location?.origin : null)
      };

      console.log('WebhookSubmissionService: Prepared data for Supabase:', webhookData);

      // Insert into Supabase
      const { data, error } = await supabase
        ?.from('webhook_submissions')
        ?.insert([webhookData])
        ?.select()
        ?.single();

      if (error) {
        console.error('Supabase insertion error:', error);
        
        if (error?.code === '23505') { // Unique violation
          return { error: { message: 'A submission with this email already exists.' } };
        }
        
        // More specific error handling
        if (error?.code === '42703') { // Column does not exist
          console.error('Database schema issue - column does not exist:', error?.message);
          return { error: { message: 'Database configuration error. Please contact support.' } };
        }
        
        if (error?.code === '23502') { // Not null violation
          console.error('Required field missing:', error?.message);
          return { error: { message: 'Required information is missing. Please check all fields.' } };
        }
        
        return { error: { message: error?.message || 'Failed to submit form' } };
      }

      console.log('WebhookSubmissionService: Successfully inserted data:', data);
      return { data };
      
    } catch (error) {
      console.error('WebhookSubmissionService: Network/connection error:', error);
      
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          error: { 
            message: 'Cannot connect to database. Please check your connection and try again.' 
          } 
        };
      }
      
      return { error: { message: 'Network error. Please try again later.' } };
    }
  }

  /**
   * Submit full application form data
   * @param {Object} applicationData - Full application form data
   * @returns {Promise<{data?, error?}>}
   */
  async submitApplicationForm(applicationData) {
    try {
      console.log('WebhookSubmissionService: Starting application form submission', applicationData);
      
      // Validate required fields for full application
      const requiredFields = [
        'fullName', 'email', 'phone', 'companyName',
        'licenseNumber', 'licenseType', 'tradeSpecialty', 'yearsExperience',
        'serviceAreas', 'currentRevenue', 'agreeToTerms', 'agreeToBackground'
      ];

      for (const field of requiredFields) {
        if (applicationData?.[field] === undefined || applicationData?.[field] === null || 
            (Array.isArray(applicationData?.[field]) && applicationData?.[field]?.length === 0) ||
            (typeof applicationData?.[field] === 'string' && !applicationData?.[field]?.trim()) ||
            (typeof applicationData?.[field] === 'boolean' && !applicationData?.[field])) {
          console.error(`Missing or invalid required field: ${field}`, applicationData?.[field]);
          return { error: { message: `Missing required field: ${field}` } };
        }
      }

      // Prepare comprehensive data for webhook table
      const webhookData = {
        full_name: applicationData?.fullName,
        email: applicationData?.email,
        phone: applicationData?.phone,
        company_name: applicationData?.companyName,
        
        form_source: 'application_form',
        submission_type: 'full_application',
        
        license_number: applicationData?.licenseNumber,
        license_type: applicationData?.licenseType,
        trade_specialty: applicationData?.tradeSpecialty,
        years_experience: applicationData?.yearsExperience,
        insurance_provider: applicationData?.insuranceProvider || null,
        
        service_areas: Array.isArray(applicationData?.serviceAreas) ? applicationData?.serviceAreas : [],
        current_revenue: applicationData?.currentRevenue,
        marketing_spend: applicationData?.marketingSpend || null,
        business_challenges: Array.isArray(applicationData?.businessChallenges) ? applicationData?.businessChallenges : [],
        
        availability: applicationData?.availability || null,
        preferred_job_types: Array.isArray(applicationData?.preferredJobTypes) ? applicationData?.preferredJobTypes : [],
        team_size: applicationData?.teamSize || null,
        agree_to_terms: Boolean(applicationData?.agreeToTerms),
        agree_to_background: Boolean(applicationData?.agreeToBackground),
        
        status: 'pending',
        source: 'landing_page',
        
        // UTM and referrer tracking
        utm_source: applicationData?.utmSource || (typeof window !== 'undefined' ? new URLSearchParams(window?.location?.search)?.get('utm_source') : null),
        utm_medium: applicationData?.utmMedium || (typeof window !== 'undefined' ? new URLSearchParams(window?.location?.search)?.get('utm_medium') : null),
        utm_campaign: applicationData?.utmCampaign || (typeof window !== 'undefined' ? new URLSearchParams(window?.location?.search)?.get('utm_campaign') : null),
        referrer: applicationData?.referrer || (typeof window !== 'undefined' && typeof document !== 'undefined' ? document?.referrer || window?.location?.origin : null)
      };

      console.log('WebhookSubmissionService: Prepared application data for Supabase:', webhookData);

      const { data, error } = await supabase
        ?.from('webhook_submissions')
        ?.insert([webhookData])
        ?.select()
        ?.single();

      if (error) {
        console.error('Supabase application insertion error:', error);
        
        if (error?.code === '23505') { // Unique violation
          return { error: { message: 'An application with this email already exists.' } };
        }
        
        if (error?.code === '42703') { // Column does not exist
          console.error('Database schema issue - column does not exist:', error?.message);
          return { error: { message: 'Database configuration error. Please contact support.' } };
        }
        
        if (error?.code === '23502') { // Not null violation
          console.error('Required field missing:', error?.message);
          return { error: { message: 'Required information is missing. Please check all fields.' } };
        }
        
        return { error: { message: error?.message || 'Failed to submit application' } };
      }

      console.log('WebhookSubmissionService: Successfully inserted application data:', data);
      return { data };
      
    } catch (error) {
      console.error('WebhookSubmissionService: Application form network error:', error);
      
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          error: { 
            message: 'Cannot connect to database. Please check your connection and try again.' 
          } 
        };
      }
      
      return { error: { message: 'Network error. Please try again later.' } };
    }
  }

  /**
   * Get submission by email
   * @param {string} email - Submission email
   * @returns {Promise<{data?, error?}>}
   */
  async getSubmissionByEmail(email) {
    try {
      if (!email) {
        return { error: { message: 'Email is required' } };
      }

      const { data, error } = await supabase
        ?.from('webhook_submissions')
        ?.select('*')
        ?.eq('email', email)
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      if (error) {
        if (error?.code === 'PGRST116') { // No rows returned
          return { error: { message: 'No submission found for this email' } };
        }
        return { error: { message: error?.message || 'Failed to fetch submission' } };
      }

      return { data };
    } catch (error) {
      return { error: { message: 'Network error. Please try again later.' } };
    }
  }

  /**
   * Get all webhook submissions (for admin/analytics)
   * @param {Object} filters - Optional filters
   * @returns {Promise<{data?, error?}>}
   */
  async getAllSubmissions(filters = {}) {
    try {
      let query = supabase?.from('webhook_submissions')?.select('*');

      // Apply filters
      if (filters?.formSource) {
        query = query?.eq('form_source', filters?.formSource);
      }
      
      if (filters?.submissionType) {
        query = query?.eq('submission_type', filters?.submissionType);
      }
      
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      
      if (filters?.dateFrom) {
        query = query?.gte('submitted_at', filters?.dateFrom);
      }
      
      if (filters?.dateTo) {
        query = query?.lte('submitted_at', filters?.dateTo);
      }

      // Default ordering by submission date
      query = query?.order('submitted_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        return { error: { message: error?.message || 'Failed to fetch submissions' } };
      }

      return { data };
    } catch (error) {
      return { error: { message: 'Network error. Please try again later.' } };
    }
  }

  /**
   * Get webhook submissions statistics
   * @returns {Promise<{data?, error?}>}
   */
  async getSubmissionStats() {
    try {
      const { data: submissions, error } = await this.getAllSubmissions();
      if (error) {
        return { error };
      }

      const stats = {
        total: submissions?.length || 0,
        heroFormLeads: submissions?.filter(sub => sub?.form_source === 'hero_form')?.length || 0,
        fullApplications: submissions?.filter(sub => sub?.form_source === 'application_form')?.length || 0,
        pending: submissions?.filter(sub => sub?.status === 'pending')?.length || 0,
        processed: submissions?.filter(sub => sub?.status !== 'pending')?.length || 0,
        today: submissions?.filter(sub => {
          const today = new Date();
          const submissionDate = new Date(sub?.created_at);
          return submissionDate?.toDateString() === today?.toDateString();
        })?.length || 0
      };

      return { data: stats };
    } catch (error) {
      return { error: { message: 'Network error. Please try again later.' } };
    }
  }

  /**
   * Update submission status (for tracking webhook processing)
   * @param {string} submissionId - Submission UUID
   * @param {string} status - New status
   * @param {string} reviewNotes - Optional notes
   * @returns {Promise<{data?, error?}>}
   */
  async updateSubmissionStatus(submissionId, status, reviewNotes = null) {
    try {
      if (!submissionId || !status) {
        return { error: { message: 'Submission ID and status are required' } };
      }

      const updateData = {
        status,
        review_notes: reviewNotes,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase
        ?.from('webhook_submissions')
        ?.update(updateData)
        ?.eq('id', submissionId)
        ?.select()
        ?.single();

      if (error) {
        return { error: { message: error?.message || 'Failed to update submission' } };
      }

      return { data };
    } catch (error) {
      return { error: { message: 'Network error. Please try again later.' } };
    }
  }

  /**
   * Get webhook feed view (formatted for Make.com integration)
   * @param {Object} filters - Optional filters
   * @returns {Promise<{data?, error?}>}
   */
  async getWebhookFeed(filters = {}) {
    try {
      let query = supabase?.from('webhook_submissions_feed')?.select('*');

      // Apply filters
      if (filters?.formSource) {
        query = query?.eq('form_source', filters?.formSource);
      }
      
      if (filters?.submissionType) {
        query = query?.eq('submission_type', filters?.submissionType);
      }

      // Default ordering by creation date
      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        return { error: { message: error?.message || 'Failed to fetch webhook feed' } };
      }

      return { data };
    } catch (error) {
      return { error: { message: 'Network error. Please try again later.' } };
    }
  }
}

export const webhookSubmissionService = new WebhookSubmissionService();
export default webhookSubmissionService;